import { NETWORK, type ChatBroadcastMessage } from "@mucki/shared";

export interface ChatOptions {
  onSend(text: string): void;
  /** Movement and abilities are suspended while the field has focus. */
  onFocusChange(focused: boolean): void;
}

export interface Chat {
  append(message: ChatBroadcastMessage): void;
  isFocused(): boolean;
  dispose(): void;
}

/**
 * `Enter` opens the field and sends on the second press; `Escape` always closes
 * it and returns control in one press. Player-authored text is written with
 * `textContent`, so markup in a message renders as literal characters (R29).
 */
export function createChat(options: ChatOptions, parent: HTMLElement = document.body): Chat {
  const root = document.createElement("div");
  root.className = "chat";

  const log = document.createElement("ul");
  log.className = "chat-log";

  const input = document.createElement("input");
  input.type = "text";
  input.className = "chat-input";
  input.maxLength = NETWORK.chat.maxLength;
  input.placeholder = "Say something…";
  input.autocomplete = "off";

  const hint = document.createElement("div");
  hint.className = "chat-hint";
  hint.textContent = "Enter to chat";

  root.append(log, input, hint);
  parent.appendChild(root);

  let open = false;

  const setOpen = (next: boolean) => {
    if (open === next) return;
    open = next;
    root.classList.toggle("is-open", open);
    if (open) input.focus();
    else input.blur();
    options.onFocusChange(open);
  };

  const onWindowKeyDown = (event: KeyboardEvent) => {
    if (open || event.key !== "Enter") return;
    // Do not steal Enter from another field, such as the name entry.
    if (document.activeElement instanceof HTMLInputElement) return;
    event.preventDefault();
    setOpen(true);
  };

  const onInputKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      input.value = "";
      setOpen(false);
      return;
    }
    if (event.key !== "Enter") return;

    event.preventDefault();
    const text = input.value;
    input.value = "";
    setOpen(false);
    if (text.trim().length > 0) options.onSend(text);
  };

  window.addEventListener("keydown", onWindowKeyDown);
  input.addEventListener("keydown", onInputKeyDown);
  input.addEventListener("blur", () => setOpen(false));

  return {
    append: (message) => {
      const line = document.createElement("li");
      line.className = "chat-line";

      const name = document.createElement("span");
      name.className = "chat-line-name";
      name.textContent = `${message.name}: `;

      const text = document.createElement("span");
      text.textContent = message.text;

      line.append(name, text);
      log.appendChild(line);

      while (log.childElementCount > NETWORK.chat.maxLogEntries) {
        log.firstElementChild?.remove();
      }
      log.scrollTop = log.scrollHeight;
    },
    isFocused: () => open,
    dispose: () => {
      window.removeEventListener("keydown", onWindowKeyDown);
      root.remove();
    },
  };
}
